import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X, Calendar, Clock, ShoppingCart } from "lucide-react";
import { LOGO } from "@/lib/images";
import { NAV } from "@/lib/site";
import { cn } from "@/lib/utils";
import { useCart } from "@/lib/cart";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);

  return (
    <header className={cn(
      "sticky top-0 z-50 w-full bg-background transition-all",
      scrolled ? "border-b border-border shadow-sm" : "border-b border-transparent",
    )}>
      <div className="bg-secondary border-b border-border">
        <div className="container-page py-2 text-center text-xs font-medium text-primary">
          Open daily until 8pm
        </div>
      </div>
      <div className="container-page grid grid-cols-[1fr_auto_1fr] items-center gap-2 py-2 sm:gap-4 sm:py-3">
        <div className="flex items-center">
          <button aria-label="Open menu" aria-expanded={open} onClick={() => setOpen((s) => !s)} className="xl:hidden inline-flex h-11 w-11 items-center justify-center rounded-md border border-border">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <nav className="hidden xl:flex items-center gap-1" aria-label="Primary">
            {NAV.slice(0, 5).map((n) => (
              <Link key={n.to} to={n.to} className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent transition-colors" activeProps={{ className: "text-primary bg-accent" }}>
                {n.label}
              </Link>
            ))}
          </nav>
        </div>

        <Link to="/" aria-label="N S Physiotherapy, Home" className="flex items-center justify-center">
          <img
            src={LOGO.src}
            srcSet={LOGO.srcSet}
            sizes="160px"
            width="160"
            height="128"
            alt="N S Physiotherapy"
            className="h-20 w-auto object-contain sm:h-24 md:h-32"
            decoding="async"
          />
        </Link>

        <div className="flex items-center justify-end gap-1.5 sm:gap-2">
          <nav className="hidden xl:flex items-center gap-1" aria-label="Primary secondary">
            {NAV.slice(5, 8).map((n) => (
              <Link key={n.to} to={n.to} className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent transition-colors" activeProps={{ className: "text-primary bg-accent" }}>
                {n.label}
              </Link>
            ))}
          </nav>
          <button
            aria-label={`Open cart (${count} items)`}
            onPointerEnter={() => void import("@/components/CartDrawer")}
            onFocus={() => void import("@/components/CartDrawer")}
            onClick={() => {
              void import("@/components/CartDrawer");
              setCartOpen(true);
            }}
            className="relative inline-flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full border border-border text-primary hover:bg-accent"
          >
            <ShoppingCart className="h-4 w-4" />
            {count > 0 && (
              <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                {count}
              </span>
            )}
          </button>
          <Link to="/booking" aria-label="Book an appointment" className="btn-hero inline-flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-3.5 text-sm font-semibold sm:h-11 sm:px-5">
            <Calendar className="hidden h-4 w-4 sm:block" /> Book
          </Link>
        </div>
      </div>


      {open && (
        <div className="xl:hidden border-t border-border bg-background">
          <nav className="container-page py-4 grid gap-1" aria-label="Mobile">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="rounded-md px-3 py-3 text-sm font-medium text-foreground hover:bg-accent" activeProps={{ className: "bg-accent text-primary" }}>
                {n.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
